import React, { useState } from 'react';

import { evictionService } from '../../services/api';

interface SittingTenantRiskProps {
    caseData: any;
    onUpdate?: () => void;
}

export default function SittingTenantRisk({ caseData, onUpdate }: SittingTenantRiskProps) {
    const [acknowledged, setAcknowledged] = useState(caseData?.riskAcknowledged || false);
    const [submitting, setSubmitting] = useState(false);

    const handleAcknowledge = async () => {
        try {
            setSubmitting(true);
            await evictionService.acknowledgeRisk(caseData.id);
            setAcknowledged(true);
            if (onUpdate) onUpdate();
        } catch (error) {
            console.error('Failed to acknowledge risk:', error);
            alert('Failed to save acknowledgement. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 mt-6 mb-6">
            <div className="flex items-start">
                <div className="flex-shrink-0">
                    {/* F-023: Alert icon for risk warning */}
                    <i className="fas fa-exclamation-triangle text-amber-500 text-xl mt-1"></i>
                </div>
                <div className="ml-3">
                    <h3 className="text-sm font-bold text-amber-900 uppercase tracking-wide">
                        Sitting Tenant Risk Mitigation
                    </h3>
                    <div className="mt-2 text-sm text-amber-800 space-y-2">
                        <p>
                            While the eviction process is underway, the tenant retains their legal right to occupy the property until a court bailiff executes a warrant of possession. Please adhere to the following strict guidelines to avoid criminal offences under the Protection from Eviction Act 1977.
                        </p>
                        <ul className="list-disc pl-5 space-y-1">
                            <li><strong>Do not change the locks</strong> or restrict access to the property.</li>
                            <li><strong>Do not cut off utilities</strong> (water, gas, electricity).</li>
                            <li><strong>Continue to fulfill repair obligations</strong> under Section 11 of the Landlord and Tenant Act 1985.</li>
                            <li><strong>Do not harass the tenant.</strong> Unannounced visits, threats, or demanding they leave before the court order can be considered harassment.</li>
                            <li><strong>Provide 24 hours written notice</strong> for any necessary visits or inspections.</li>
                        </ul>
                    </div>
                </div>
            </div>

            {!acknowledged ? (
                <div className="mt-5 ml-8">
                    {/* F-023: Acknowledgement functionality for Landlords */}
                    <button
                        onClick={handleAcknowledge}
                        disabled={submitting}
                        className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-md text-xs font-semibold shadow-sm transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {submitting ? (
                            <><i className="fas fa-spinner fa-spin mr-2"></i> Saving...</>
                        ) : (
                            <><i className="fas fa-check mr-2"></i> I acknowledge these legal restrictions</>
                        )}
                    </button>
                </div>
            ) : (
                <div className="mt-5 ml-8 flex items-center text-sm font-bold text-emerald-700 bg-emerald-50 px-3 py-2 rounded-md w-fit border border-emerald-200">
                    <i className="fas fa-check-circle mr-2"></i> Restrictions acknowledged. Risk mitigated.
                </div>
            )}
        </div>
    );
}
